import { useMemo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { LightningBolt } from '@/components/icons/lightning-bolt';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { useOpeningContext } from '@/contexts/opening-context';
import { cn } from '@/utils/classnames';
import { isWithinLast24Hours, toDate } from '@/utils/date-util';

import { PerformanceStatusBanner } from '../performance-status-banner';
import { useChannelsStore } from '../use-channels-store';
import { useAutomationMessages } from './use-automation-messages';

import type { ReactNode } from 'react';

function AutomationStatusBanner() {
  const { hiringGoal, analyticsData, hasActiveHiringGoal, hiringGoalUrl } =
    useOpeningContext();
  const channel = useChannelsStore(s => s.lastAutomationChannel);
  const { getAutomationMessages } = useAutomationMessages();
  const { openEditTargetsDialog } = useEditTargetsContext();

  const lastAutomationReason = channel?.deactivation_reason ?? null;
  const isActive = !channel?.deactivated_at;
  const lastAutomationTriggerAt = toDate(
    channel?.automation_triggered_at ?? null,
  );

  const handleEditTargets = () => {
    openEditTargetsDialog({
      targets: {
        conversion: analyticsData?.conversion_stats?.expected ?? null,
        budget: analyticsData?.budget_stats?.expected ?? null,
        cpa: analyticsData?.cpa_stats?.expected ?? null,
      },
    });
  };

  const automationCopy = useMemo(() => {
    const copy = getAutomationMessages(lastAutomationReason);

    let title: ReactNode = isActive ? copy.activeTitle : copy.pausedTitle;
    const description = isActive
      ? copy.activeDescription
      : copy.pausedDescription;

    if (copy.showDateInTitle && lastAutomationTriggerAt) {
      title = (
        <>
          {title} –{' '}
          <FormattedDate
            value={lastAutomationTriggerAt}
            month="short"
            day="2-digit"
            year="numeric"
          />
        </>
      );
    }

    return {
      title,
      description,
    };
  }, [
    getAutomationMessages,
    lastAutomationReason,
    isActive,
    lastAutomationTriggerAt,
  ]);

  // For past hiring goal we should not show funnel/budget automation messages
  if (
    !channel ||
    (!hiringGoal?.is_current && lastAutomationReason !== 'hiring_goal_ended')
  ) {
    return <PerformanceStatusBanner />;
  }

  // If the automation was not triggered in the last 24 hours, we should not show the banner.
  if (
    !lastAutomationTriggerAt ||
    !(lastAutomationTriggerAt && isWithinLast24Hours(lastAutomationTriggerAt))
  ) {
    return null;
  }

  // For past hiring goal we should not show hiring automation message if there is an active hiring goal present.
  if (
    lastAutomationReason === 'hiring_goal_ended' &&
    !isActive &&
    hasActiveHiringGoal
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        'border-1 flex items-center gap-2 self-stretch rounded-md p-2',
        isActive
          ? 'bg-fx-blue-50 border-fx-blue'
          : 'bg-fx-rose-50 border-fx-rose',
      )}
    >
      <LightningBolt
        className={cn('h-4 w-4', isActive ? 'text-fx-blue' : 'text-fx-rose')}
      />
      <div className="flex grow flex-col items-start justify-center">
        <Typography variant="body2">{automationCopy.title}</Typography>
        <Typography variant="body1">{automationCopy.description}</Typography>
      </div>
      {lastAutomationReason === 'hiring_goal_ended' &&
        !isActive &&
        hiringGoalUrl && (
          <Button variant="secondary" asChild>
            <Link to={String(hiringGoalUrl)} target="_blank">
              <FormattedMessage defaultMessage="Set new hiring goal" />
            </Link>
          </Button>
        )}
      {lastAutomationReason === 'target_budget_met' && !isActive && (
        <Button variant="secondary" onClick={handleEditTargets}>
          <FormattedMessage defaultMessage="Update Budget" />
        </Button>
      )}
    </div>
  );
}

export { AutomationStatusBanner };
